import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { health as fetchHealth } from '../api/endpoints';
import type { HealthResponse } from '../api/types';

export type ConnectionState = 'checking' | 'online' | 'offline';

interface HealthValue {
  health: HealthResponse | null;
  connection: ConnectionState;
  error: string | null;
  lastCheckedAt: string | null;
  refresh: () => Promise<void>;
}

const HealthContext = createContext<HealthValue | null>(null);

const POLL_MS = 5_000;

/**
 * `/v1/health` is the one endpoint that needs no key, which makes it the honest
 * answer to "is there a server there at all?": the login screen shows it before
 * anyone has signed in. It also carries the live queue counters and the version
 * banner, so it is polled rather than fetched once.
 */
export function HealthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('checking');
  const [error, setError] = useState<string | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const next = await fetchHealth();
      setHealth(next);
      setConnection('online');
      setError(null);
    } catch (cause) {
      setConnection('offline');
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLastCheckedAt(new Date().toISOString());
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const value = useMemo<HealthValue>(
    () => ({ health, connection, error, lastCheckedAt, refresh }),
    [health, connection, error, lastCheckedAt, refresh],
  );

  return <HealthContext.Provider value={value}>{children}</HealthContext.Provider>;
}

export function useHealth(): HealthValue {
  const value = useContext(HealthContext);
  if (!value) throw new Error('useHealth must be used inside <HealthProvider>');
  return value;
}